<script setup lang="ts">
import { IconLogo } from '@/shared/ui/icons';

/** Экран техработ поверх открытой вкладки; повторяет `docker/edge/http.d/maintenance.html`. */
defineProps<{ actionLost: boolean }>();
</script>

<template>
  <div
    class="maintenance-screen fixed inset-0 z-[9999] grid place-items-center overflow-y-auto px-3 py-4 text-(--base-text-color) sm:px-4 sm:py-6"
    role="alert"
  >
    <section
      class="maintenance-card relative w-full max-w-160 overflow-hidden rounded-[22px] border border-white/8 px-5 py-8 text-center sm:rounded-[28px] sm:px-16 sm:py-14"
    >
      <div
        class="logo-orbit relative mx-auto mb-3.5 grid size-28 place-items-center sm:mb-4 sm:size-32"
        aria-hidden="true"
      >
        <IconLogo
          :width="71"
          :height="71"
          class="logo-mark text-(--sidebar-logo-color)"
        />
      </div>

      <p class="mb-3.5 text-xs leading-snug font-bold tracking-[0.16em] text-(--base-accent-color) uppercase">
        Делаем Морс лучше
      </p>
      <h1 class="mx-auto mb-4 text-[clamp(2.25rem,8vw,4rem)] leading-[1.1] font-bold tracking-[-0.03em] text-balance">
        Одну минуту
      </h1>
      <p
        class="mx-auto max-w-[48ch] text-base leading-normal text-pretty text-(--base-secondary-text-color) sm:text-lg"
      >
        Обновляем Морс, чтобы работать стало ещё удобнее. Это ненадолго, спасибо, что подождёте.
      </p>
      <p
        v-if="actionLost"
        class="mx-auto mt-4 max-w-[48ch] text-sm leading-normal text-(--base-warning-color)"
      >
        Последнее действие не выполнено. Повторите его после обновления.
      </p>
      <p
        class="mx-auto mt-6.5 flex w-fit max-w-full items-start gap-3 text-left text-sm leading-[1.45] text-(--base-secondary-text-color) sm:mt-7 sm:items-center"
      >
        <span
          class="waiting-indicator mt-[0.48em] inline-flex shrink-0 items-center gap-1 sm:mt-0"
          aria-hidden="true"
        >
          <span />
          <span />
          <span />
        </span>
        <span>Страница обновится сама, когда всё будет готово</span>
      </p>
    </section>
  </div>
</template>

<style scoped>
.maintenance-screen {
  background:
    radial-gradient(
      circle at 50% 12%,
      color-mix(in srgb, var(--base-accent-color) 16%, transparent),
      transparent 36rem
    ),
    radial-gradient(
      circle at 12% 88%,
      color-mix(in srgb, var(--sidebar-logo-color) 10%, transparent),
      transparent 28rem
    ),
    var(--base-main-bg);
}

.maintenance-card {
  background: linear-gradient(145deg, rgb(255 255 255 / 3.5%), transparent 42%), var(--base-container-bg);
  box-shadow:
    0 28px 72px rgb(8 11 22 / 30%),
    inset 0 1px rgb(255 255 255 / 4%);
}

.maintenance-card::after {
  position: absolute;
  inset: 0 auto auto 50%;
  width: 42%;
  height: 1px;
  content: '';
  background: linear-gradient(90deg, transparent, var(--base-accent-color), transparent);
  transform: translateX(-50%);
}

.logo-orbit {
  isolation: isolate;
}

.logo-orbit::before,
.logo-orbit::after {
  position: absolute;
  content: '';
  border-radius: 50%;
}

.logo-orbit::before {
  inset: 0;
  z-index: -2;
  background: conic-gradient(
    from 18deg,
    transparent 0 17%,
    color-mix(in srgb, var(--base-accent-color) 85%, transparent) 26%,
    color-mix(in srgb, var(--sidebar-logo-color) 85%, transparent) 43%,
    transparent 52% 100%
  );
  mask: radial-gradient(farthest-side, transparent calc(100% - 2px), #000 0);
  animation: orbit 12s linear infinite;
}

.logo-orbit::after {
  inset: 17px;
  z-index: -1;
  background: color-mix(in srgb, var(--sidebar-logo-color) 16%, transparent);
  filter: blur(16px);
  animation: halo 4.8s ease-in-out infinite;
}

.logo-mark {
  filter: drop-shadow(0 9px 24px color-mix(in srgb, var(--sidebar-logo-color) 22%, transparent));
  animation: breathe 4.8s ease-in-out infinite;
}

.waiting-indicator span {
  width: 5px;
  height: 5px;
  background: var(--base-accent-color);
  border-radius: 50%;
  animation: waiting 1.8s ease-in-out infinite;
}

.waiting-indicator span:nth-child(2) {
  animation-delay: 0.18s;
}

.waiting-indicator span:nth-child(3) {
  animation-delay: 0.36s;
}

@keyframes orbit {
  to {
    transform: rotate(1turn);
  }
}

@keyframes halo {
  0%,
  100% {
    opacity: 0.55;
    transform: scale(0.9);
  }

  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}

@keyframes breathe {
  0%,
  100% {
    transform: translateY(1px) scale(0.98);
  }

  50% {
    transform: translateY(-2px) scale(1.02);
  }
}

@keyframes waiting {
  0%,
  55%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }

  25% {
    opacity: 1;
    transform: translateY(-3px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .logo-orbit::before,
  .logo-orbit::after,
  .logo-mark,
  .waiting-indicator span {
    animation: none;
  }
}
</style>
